import * as T from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { MapRenderer } from './renderer.js';
import { Component } from './live-codec.js';
import { PreviewClock, overrideLibrary } from './asset-inspection.js';
import { texturePath } from './binary.js';

export class AssetPreview {
    constructor(canvas, session, diagnostic, onInfo = () => {}) {
        Object.assign(this, { canvas, session, diagnostic, onInfo });
        this.clock = new PreviewClock(); this.generation = 0;
        this.view = new MapRenderer(canvas, diagnostic, {
            alpha: true,
            controls: (canvas, view) => {
                const controls = new OrbitControls(view.camera, canvas); controls.enableDamping = true;
                controls.minDistance = .1; controls.maxDistance = 50000; controls.release = () => {};
                controls.addEventListener('change', () => { const d = controls.target.clone().sub(view.camera.position); view.yaw = Math.atan2(d.y, d.x); view.pitch = Math.atan2(d.z, Math.hypot(d.x,d.y)); });
                return controls;
            },
            previewClock: (_, dt) => { const time = this.clock.tick(dt, document.hidden || this.loadingPreview || [...(this.live?.particles.effects.values() ?? [])].some(e => e.pending)); this.beforeFrame?.(); return time; },
        });
        this.view.onLiveFrame = () => this.afterFrame();
        this.view.renderer.setClearColor(0x000000, 1);
    }
    resetSettings() {
        this.options = { skin: 0, body: 0, sequence: 0, poses: [], scale: 1, loop: true, wireframe: false, skeleton: false, attachments: false, bounds: false,
            bind: false, frame: 0, fps: 10, mode: 2, tint: '#ffffff', opacity: 1, channel: 'rgb', tile: [1,1], offset: [0,0], seed: 1, repeat: false,
            light: 1, lightYaw: 45, lightPitch: 45, ambient: 1, lightColor: '#ffffff', gridSpacing: 10,
            position: [0,0,0], angles: [0,0,0], surface: 'material', sampling: 'linear', uvRotation: 0, shape: 'plane' };
        this.controlPoints = [{ id: 0, position: [0,0,0], angles: [0,0,0], parent: -1, motion: 'static', speed: 30, radius: 30 }];
        this.overrides = {}; this.disabled = new Set(); this.clock.restart(); this.clock.paused = false; this.clock.speed = 1;
    }
    clear() {
        this.entry = null; this.options = null;
        this.savedCamera = null; this.originalMaterials = new WeakMap();
        this.normalMaterial = null;
        this.generation++; this.live = null; this.card = null; this.texture = null; this.library = null; this.metadata = null;
        this.helpers?.traverse(o => { o.geometry?.dispose(); if (o.material) for (const m of Array.isArray(o.material) ? o.material : [o.material]) m.dispose(); });
        this.helpers = null; this.markers = null; this.box = null; this.skeletonHelper = null; this.skeletonEntity = null; this.view.clear();
        this.view.renderer.setClearColor(0x000000, 1);
    }
    async select(entry, { sprite = false } = {}) {
        this.session.activatePreview();
        this.clear(); this.resetSettings(); this.loadingPreview = true; this.entry = entry; this.kind = sprite ? 'sprite' : entry.kind;
        const own = this.generation, active = () => own === this.generation;
        this.rpc = async (type, args) => {
            if (!active()) throw new DOMException('Cancelled', 'AbortError');
            if (type === 'particle' && this.library) return { library: overrideLibrary(this.library, this.overrides, this.disabled) };
            const result = await this.session.rpc(type, args);
            if (!active()) throw new DOMException('Cancelled', 'AbortError');
            return result;
        };
        this.view.controls.enableRotate = !['material','texture'].includes(this.kind);
        this.helpers = new T.Group(); this.view.scene.add(this.helpers);
        const grid = new T.GridHelper(200, 20, 0x75879a, 0x394450); grid.rotation.x = Math.PI / 2; grid.visible = true; this.grid = grid; this.helpers.add(grid);
        this.axes = new T.AxesHelper(30); this.axes.visible = false; this.helpers.add(this.axes);
        this.ambient = new T.AmbientLight(0xffffff, 1); this.view.scene.add(this.ambient);
        this.light = new T.DirectionalLight(0xffffff, 1); this.light.position.set(100,-100,100); this.view.scene.add(this.light);
        if (this.kind === 'model') {
            this.metadata = await this.rpc('model-inspection', { path: entry.path });
            for (const message of this.metadata.library.diagnostics ?? []) this.diagnostic(message);
            this.options.poses = this.metadata.library.poses.map(p => p.end === p.start ? 0 : T.MathUtils.clamp((0 - p.start) / (p.end - p.start),0,1));
            this.startLive(); this.applyEntity();
        } else if (this.kind === 'sprite') {
            const source = await this.rpc('sprite', { path: entry.path, s3tc: this.view.s3tc });
            this.metadata = { frames: source.texture.frameFaces?.length ?? 1, width: source.texture.width, height: source.texture.height };
            this.startLive(); this.applyEntity();
        } else if (this.kind === 'particle') {
            this.metadata = await this.rpc('particle-definitions', { path: entry.path });
            if (!this.metadata.definitions.length) throw new Error('This PCF contains no particle systems.');
            await this.particle(this.metadata.definitions[0].id);
        } else {
            let material;
            if (this.kind === 'material') {
                const { material: source } = await this.rpc('material', { path: entry.path });
                this.metadata = source;
                material = await this.view.material(entry.path, this.rpc);
                if (source.values.$basetexture) this.texture = await this.view.texture(texturePath(source.values.$basetexture), this.rpc, { animation: true });
            } else {
                this.texture = await this.view.texture(entry.path, this.rpc, { animation: true });
                material = this.view.track(new T.MeshBasicMaterial({ map: this.texture, transparent: true, side: T.DoubleSide }));
            }
            if (!active()) return;
            const image = this.texture?.image ?? {}, width = image.width ?? 128, height = image.height ?? 128;
            this.cardSize = [width,height]; this.metadata = { ...this.metadata, width, height, frames: this.texture?.userData.animationFrames?.length ?? 1 };
            const geometry = this.view.track(new T.PlaneGeometry(width,height)); geometry.rotateX(Math.PI/2);
            geometry.setAttribute('uv1', geometry.attributes.uv.clone()); geometry.setAttribute('blend', new T.Float32BufferAttribute([0,0,0,0],1));
            this.card = new T.Mesh(geometry, material); this.cardBase = material; this.baseTint = material.color?.clone() ?? new T.Color(1,1,1); this.baseOpacity = material.opacity;
            this.baseTransparent = material.transparent;
            this.options.tile = material.map?.repeat.toArray() ?? [1,1]; this.options.offset = material.map?.offset.toArray() ?? [0,0];
            this.textureCenter = material.map?.center.clone() ?? new T.Vector2(); this.textureRotation = material.map?.rotation ?? 0;
            this.channelMaterial = this.view.track(new T.ShaderMaterial({
                uniforms: { map: { value: this.texture }, channel: { value: -1 }, mapTransform: { value: new T.Matrix3() } },
                vertexShader: 'uniform mat3 mapTransform; varying vec2 vUv; void main(){vUv=(mapTransform*vec3(uv,1.)).xy; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}',
                fragmentShader: 'uniform sampler2D map; uniform int channel; varying vec2 vUv; void main(){vec4 c=texture2D(map,vUv); float v=channel==0?c.r:channel==1?c.g:channel==2?c.b:c.a; gl_FragColor=vec4(vec3(v),1.);\n#include <colorspace_fragment>\n}', side: T.DoubleSide,
            }));
            this.view.scene.add(this.card);
        }
        if (!active()) return;
        this.needsFit = this.kind === 'model' || this.kind === 'sprite';
        if (!this.needsFit) this.fit();
        if (this.live) {
            while (active() && (this.live.running || this.live.pendingModels.size || [...this.live.particles.effects.values()].some(e => e.pending)))
                await new Promise(resolve => requestAnimationFrame(resolve));
            if (!active()) return;
            if (this.kind === 'model' && this.live.entities.get('1:1')?.loadingPlaceholder)
                throw new Error('Model dependencies could not be loaded. See Diagnostics.');
            if (this.kind === 'sprite' && !this.live.entities.get('1:1')?.sprite) throw new Error('Sprite could not be loaded. See Diagnostics.');
        }
        this.fit(); this.needsFit = false; this.loadingPreview = false; this.clock.restart(); this.background(this.backgroundValue ?? "dark"); this.onInfo(this.metadata);
    }
    startLive() {
        this.live = this.view.startLive(this.rpc, { now: () => this.clock.time });
        this.live.timeline.time = this.live.timeline.animationTime = () => this.clock.time;
    }
    async particle(id) {
        const request = this.particleRequest = (this.particleRequest ?? 0) + 1;
        const { library } = await this.rpc('particle-file', { path: this.entry.path, id });
        if (request !== this.particleRequest) return;
        this.library = library; this.particleId = id; this.overrides = {}; this.disabled.clear(); this.restart(); this.onInfo(this.metadata);
    }
    restart() {
        this.clock.restart();
        if (this.kind === 'particle') { this.startLive(); this.applyEntity(); }
        else { this.live?.particles.clear(); this.applyEntity(); }
    }
    stopEmission() { for (const effect of this.live?.particles.effects.values() ?? []) this.live.particles.stop(effect); }
    applyEntity() {
        if (!this.live) return;
        const o = this.options, clip = this.metadata?.library?.sequences[o.sequence]?.clips[0];
        const duration = clip ? Math.max(.001, (clip.frames - 1) / Math.max(.001, clip.fps)) : 1;
        const color = parseInt(o.tint.slice(1),16), packed = ((Math.round(o.opacity*255)<<24) | ((color&255)<<16) | (color&0xff00) | (color>>>16)) >>> 0;
        const particle = this.kind === 'particle';
        const components = {
            [Component.Transform]: { position: o.position, angles: o.angles, velocity: [0,0,0] },
            [Component.Appearance]: { model: particle ? 0 : 1, category: particle ? 9 : 2, skin: o.skin, body: o.body, scale: o.scale, effects: 0, color: packed, renderMode: this.kind === 'sprite' ? o.mode : 0 },
        };
        if (this.kind === 'model') components[Component.Animation] = { sequence: o.sequence, flags: 8 | (o.loop ? 16 : 0), cycle: 0, anchorTime: 0, cycleRate: 1/duration, rate: 1, poses: o.poses };
        if (this.kind === 'sprite') components[Component.Sprite] = { frame: o.frame, framerate: o.fps, scale: 1, brightness: 255, flags: 0 };
        if (particle) components[Component.Particle] = { effect: 1, flags: 1, startTime: 0, previewSeed: o.seed, attachType: 0, offset: [0,0,0], controlPoints: this.pointStates() };
        this.row = { key: '1:1', index: 1, generation: 1, sampleTime: 0, history: [], components };
        this.live.apply({ epoch: 1n, sampleTime: 0, changes: [this.row], deleted: [], dictionary: [['1:1',this.entry.path],['3:1','asset-preview']] });
    }
    pointStates() {
        const seconds = this.clock.time/1000;
        const result = [];
        for (const cp of this.controlPoints) {
            const position = [...cp.position];
            if (cp.motion === 'linear') position[0] += seconds * cp.speed;
            if (cp.motion === 'circle') { const a = seconds * cp.speed * Math.PI/180; position[0] += Math.cos(a)*cp.radius; position[1] += Math.sin(a)*cp.radius; }
            const m = new T.Matrix4().makeRotationFromEuler(new T.Euler(...cp.angles.map(a => a*Math.PI/180),'ZYX')), e = m.elements;
            m.setPosition(...position);
            const parent = result.find(point => point.id === cp.parent);
            if (parent) m.premultiply(parent.matrix);
            result.push({ id: cp.id, position: new T.Vector3().setFromMatrixPosition(m).toArray(), basis: [[e[0],e[1],e[2]],[-e[4],-e[5],-e[6]],[e[8],e[9],e[10]]], parent: cp.parent, matrix: m });
        }
        return result.map(({matrix, ...point}) => point);
    }
    beforeFrame() {
        if (!this.live || this.kind !== 'particle') return;
        for (const effect of this.live.particles.effects.values()) effect.spec.controlPoints = this.pointStates();
    }
    afterFrame() {
        if (!this.options) return;
        const o = this.options, entity = this.live?.entities.get('1:1');
        if (entity && !entity.pending && !entity.loading && this.needsFit) { this.fit(); this.needsFit = false; }
        if (entity?.rig) {
            if (o.bind) { entity.rig.bones.forEach((bone,i) => { bone.position.fromArray(entity.rig.data.bones[i].position); bone.quaternion.fromArray(entity.rig.data.bones[i].quaternion); }); entity.object.updateMatrixWorld(true); for(const bounds of entity.animatedBounds ?? []) bounds.update(); }
            if (this.skeletonEntity !== entity.rig) {
                this.skeletonHelper?.removeFromParent(); this.skeletonHelper?.dispose();
                this.skeletonEntity = entity.rig; this.skeletonHelper = new T.SkeletonHelper(entity.object); this.helpers.add(this.skeletonHelper);
            }
            this.skeletonHelper.visible = o.skeleton;
        }
        entity?.object.traverse(object => {
            if (!object.material || object.isLine) return;
            if (!this.originalMaterials.has(object)) this.originalMaterials.set(object, object.material);
            if (o.surface === 'normals') {
                this.normalMaterial ??= this.view.track(new T.MeshNormalMaterial({ side: T.DoubleSide }));
                object.material = this.normalMaterial;
            } else object.material = this.originalMaterials.get(object);
            for (const material of Array.isArray(object.material) ? object.material : [object.material]) material.wireframe = o.wireframe;
        });
        if (entity && o.bounds) {
            this.boxBounds ??= new T.Box3(); this.boxBounds.makeEmpty();
            if (this.kind === 'particle') {
                const point = new T.Vector3(), radius = new T.Vector3();
                for (const effect of new Set([...this.live.particles.effects.values(), ...this.live.particles.draining]))
                    for (const node of effect.nodes) for (const particle of node.sim.particles) {
                        radius.setScalar(Math.abs(particle.radius)); point.fromArray(particle.position);
                        this.boxBounds.expandByPoint(point.clone().sub(radius)); this.boxBounds.expandByPoint(point.add(radius));
                    }
            } else this.boxBounds.setFromObject(entity.object);
            this.box ??= new T.Box3Helper(this.boxBounds,0xffaa33);
            if (!this.box.parent) this.helpers.add(this.box); this.box.visible = !this.boxBounds.isEmpty();
        } else if (this.box) this.box.visible = false;
        const points = o.attachments ? (entity?.rig?.attachments ?? []).map(a => ({ position: a.getWorldPosition(new T.Vector3()), quaternion: a.getWorldQuaternion(new T.Quaternion()) }))
            : this.kind === 'particle' && o.points ? this.pointStates().map(cp => ({ position: new T.Vector3(...cp.position), basis: cp.basis })) : [];
        if (this.helpers && (!this.markers || this.markers.children.length !== points.length)) {
            this.markers?.traverse(object => { object.geometry?.dispose(); object.material?.dispose(); }); this.markers?.removeFromParent();
            this.markers = new T.Group(); this.helpers.add(this.markers);
            for (const point of points) this.markers.add(new T.AxesHelper(10));
        }
        points.forEach((point,i) => { const axes=this.markers.children[i]; axes.position.copy(point.position); if (point.quaternion) axes.quaternion.copy(point.quaternion); else if(point.basis) axes.quaternion.setFromRotationMatrix(new T.Matrix4().makeBasis(new T.Vector3(...point.basis[0]),new T.Vector3(...point.basis[1]).negate(),new T.Vector3(...point.basis[2]))); });
        if (this.card) {
            const frames = this.texture?.userData.animationFrames ?? [this.texture];
            const texture = frames[(o.frame + Math.floor(this.clock.time/1000*o.fps)) % frames.length];
            if (texture) {
                texture.repeat.fromArray(o.tile); texture.offset.fromArray(o.offset); texture.center.copy(this.textureCenter); texture.rotation=this.textureRotation+o.uvRotation*Math.PI/180;
                const filter=o.sampling==='nearest'?T.NearestFilter:T.LinearFilter;
                if(texture.magFilter!==filter||texture.minFilter!==filter){texture.magFilter=texture.minFilter=filter;texture.needsUpdate=true;}
                this.cardBase.map = texture; this.channelMaterial.uniforms.map.value = texture;
            }
            this.cardBase.color?.copy(this.baseTint).multiply(new T.Color(o.tint)); this.cardBase.opacity = this.baseOpacity * o.opacity;
            if (this.cardBase.transparent !== (this.baseTransparent || o.opacity < 1)) { this.cardBase.transparent = this.baseTransparent || o.opacity < 1; this.cardBase.needsUpdate = true; }
            this.channelMaterial.uniforms.channel.value = ['r','g','b','a'].indexOf(o.channel);
            if(texture) { texture.updateMatrix(); this.channelMaterial.uniforms.mapTransform.value.copy(texture.matrix); }
            this.card.material = o.channel === 'rgb' || !texture ? this.cardBase : this.channelMaterial;
        }
        if (this.ambient) this.ambient.intensity = o.ambient;
        if (this.light) { this.light.color.set(o.lightColor); this.light.intensity = o.light; const yaw=o.lightYaw*Math.PI/180,pitch=o.lightPitch*Math.PI/180; this.light.position.set(Math.cos(yaw)*Math.cos(pitch),Math.sin(yaw)*Math.cos(pitch),Math.sin(pitch)).multiplyScalar(100); }
        if (this.live && this.kind === 'particle' && o.repeat && this.clock.time > 200 && !this.live.particles.diagnostics().systems && !this.live.particles.diagnostics().draining) this.restart();
    }
    fit(actual = false) {
        const object = this.card ?? this.live?.entities.get('1:1')?.object;
        const bounds = object ? new T.Box3().setFromObject(object) : new T.Box3(new T.Vector3(-50,-50,-50),new T.Vector3(50,50,50));
        if (bounds.isEmpty()) { bounds.min.set(-50,-50,-50); bounds.max.set(50,50,50); }
        const center=bounds.getCenter(new T.Vector3()), size=bounds.getSize(new T.Vector3());
        const camera=this.view.camera, flat=['material','texture'].includes(this.kind);
        const radius=Math.max(1,Math.max(size.x,size.y,size.z)/2), fov=camera.fov*Math.PI/180;
        let distance = radius / Math.sin(fov/2) / Math.min(1,camera.aspect) * 1.25;
        if (actual && flat) distance = this.canvas.clientHeight / (2*Math.tan(fov/2));
        this.view.controls.target.copy(center); camera.position.copy(center).addScaledVector(flat ? new T.Vector3(0,-1,0) : new T.Vector3(1,-1,.6).normalize(),distance);
        camera.near=Math.max(.01,distance/10000); camera.updateProjectionMatrix(); this.view.controls.update();
    }
    cameraAngle(value) {
        const directions = { perspective: [1,-1,.6], front: [1,0,0], back: [-1,0,0], left: [0,-1,0], right: [0,1,0], top: [0,0,1], bottom: [0,0,-1] };
        const target=this.view.controls.target, camera=this.view.camera;
        const distance=Math.max(.1,camera.position.distanceTo(target));
        camera.position.copy(target).addScaledVector(new T.Vector3(...directions[value]).normalize(),distance);
        // OrbitControls caches its up-axis transform; keep the Source Z-up basis.
        this.view.controls.update();
    }
    saveCamera() {
        const c=this.view.camera;
        this.savedCamera={position:c.position.clone(),up:c.up.clone(),target:this.view.controls.target.clone(),fov:c.fov};
    }
    restoreCamera() {
        const s=this.savedCamera;if(!s)return;
        const c=this.view.camera;c.position.copy(s.position);c.up.copy(s.up);c.fov=s.fov;c.updateProjectionMatrix();
        this.view.controls.target.copy(s.target);this.view.controls.update();this.onInfo(this.metadata);
    }
    surface(shape) {
        if(!this.card)return;
        this.options.shape=shape;
        const [w,h]=this.cardSize, radius=Math.max(w,h)/2;
        const geometry=shape==='sphere'?new T.SphereGeometry(radius,48,24):shape==='cube'?new T.BoxGeometry(w,w,h):new T.PlaneGeometry(w,h).rotateX(Math.PI/2);
        geometry.setAttribute('uv1',geometry.attributes.uv.clone());
        geometry.setAttribute('blend',new T.Float32BufferAttribute(new Float32Array(geometry.attributes.position.count),1));
        this.card.geometry.dispose();this.view.resources.delete(this.card.geometry);this.card.geometry=this.view.track(geometry);
        this.view.controls.enableRotate=shape!=='plane';this.fit();
    }
    background(value) {
        this.backgroundValue = value;
        this.canvas.parentElement.dataset.background = value;
        this.view.renderer.setClearColor(this.entry ? (value === 'light' ? 0xdce2e8 : 0x171d24) : 0x000000, this.entry && value === 'checker' ? 0 : 1);
    }
    dispose() { this.clear(); this.view.dispose(); }
}
